import React from 'react';
import { Box, Paper, Typography, LinearProgress } from "@mui/material";
import CreditCardIcon from '@mui/icons-material/CreditCard';

const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(Number(val || 0));
};

export default function CreditUtilizationPanel({ bankListData }) {
    let totalCreditUsed = 0;
    let totalAvailable = 0; 
    let creditAccounts = [];

    const parseAmount = (val) => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') return parseFloat(val.replace(/[^0-9.-]+/g, "") || 0);
        return 0;
    };

    if (bankListData && Array.isArray(bankListData)) {
        bankListData.forEach(bank => {
            if(bank.accounts){
                bank.accounts.forEach(acc => {
                    const absBalance = Math.abs(parseAmount(acc.total_spend));
                    const limit = acc.credit_limit > 0 ? acc.credit_limit : 10000;
                    
                    let available = acc.available_credit;
                    if (available === undefined || available === null) {
                        available = limit - absBalance;
                    }

                    if (acc.prettyType !== 'Deposit') {
                        totalCreditUsed += absBalance;
                        totalAvailable += available;
                        
                        creditAccounts.push({
                            name: acc.prettyName,
                            bank: bank.bankName,
                            mask: acc.card_number,
                            used: absBalance,
                            limit: limit,
                            percent: limit > 0 ? (absBalance / limit) * 100 : 0
                        });
                    }
                });
            }
        });
    }

    const overallTotalLimit = totalCreditUsed + totalAvailable;
    const overallUtilPercent = overallTotalLimit > 0 ? (totalCreditUsed / overallTotalLimit) * 100 : 0;

    return (
        <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            bgcolor: '#1e1e1e', 
            color: '#fff', 
            border: '1px solid #333',
            height: '100%', 
            minHeight: 500, // Force height
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
                <CreditCardIcon sx={{ color: '#fff' }} />
                <Typography variant="h6" fontWeight="bold">Credit Card Utilization</Typography>
            </Box>
            
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="#b0b3b8">Overall Utilization</Typography>
                    <Typography variant="body2" fontWeight="bold" color={overallUtilPercent > 30 ? '#ef5350' : '#66bb6a'}>
                        {overallUtilPercent.toFixed(0)}%
                    </Typography>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={overallUtilPercent} 
                    sx={{ 
                        height: 8, 
                        borderRadius: 4, 
                        bgcolor: '#333',
                        '& .MuiLinearProgress-bar': { bgcolor: overallUtilPercent > 30 ? '#ef5350' : '#66bb6a' }
                    }} 
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" color="#777">{formatCurrency(totalCreditUsed)} utilized</Typography>
                    <Typography variant="caption" color="#777">{formatCurrency(overallTotalLimit)} total limit</Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                <Typography variant="subtitle2" color="#b0b3b8" mb={2}>Highest Utilization Cards</Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    {creditAccounts.sort((a,b) => b.percent - a.percent).map((card, idx) => (
                        <Box key={idx} p={2} border="1px solid #333" borderRadius={2} bgcolor="#252525">
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">{card.name}</Typography>
                                    <Typography variant="caption" color="#b0b3b8">{card.bank} • ****{card.mask}</Typography>
                                </Box>
                                <Box textAlign="right">
                                    <Typography variant="body2" fontWeight="bold" color={card.percent > 30 ? '#ef5350' : '#66bb6a'}>
                                        {card.percent.toFixed(0)}%
                                    </Typography>
                                </Box>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={card.percent} 
                                sx={{ 
                                    height: 4, 
                                    borderRadius: 2, 
                                    bgcolor: '#333',
                                    '& .MuiLinearProgress-bar': { bgcolor: card.percent > 30 ? '#ef5350' : '#66bb6a' }
                                }} 
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
}